import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {Lightbox} from '@Components/lightbox/lightbox';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, Size, Type} from '@Components/button';
import {PdfCanvas} from '@Components/pdf-canvas/pdf-canvas';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {downloadPdf, isBesidePdfPage, resolveDocumentTitle} from './pdf-lightbox.library';
import type {PdfLightboxProps} from './pdf-lightbox.types';
import styles from './pdf-lightbox.module.scss';

export function PdfLightbox({isOpen, url, title, onClose}: PdfLightboxProps): ReactElement | null {
  const [isDownloading, setIsDownloading] = useState(false);
  const mediaContainerRef = useRef<HTMLDivElement | null>(null);
  const isSmall = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.LARGE);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || url === undefined || url === '') {
    return null;
  }

  const documentTitle = resolveDocumentTitle(title);

  const openInNewTab = (): void => {
    window.open(url, '_blank', 'noopener');
  };

  const download = async (): Promise<void> => {
    setIsDownloading(true);
    try {
      await downloadPdf(url, documentTitle);
    } finally {
      setIsDownloading(false);
    }
  };

  const renderOpenInNewTabAction = (): ReactElement => {
    if (isSmall) {
      return (
        <Icon
          icon="icon-external-link"
          size={IconSize.SIZE_ICON_16}
          type={IconType.DARK_SECONDARY}
          shape={IconShape.SQUARE}
          ariaLabel={window.i18next.t('pmwjs_open_in_new_tab')}
          onClick={openInNewTab}
        />
      );
    }

    return <Button text={window.i18next.t('pmwjs_open_in_new_tab')} size={Size.XSMALL} type={Type.DARK_SECONDARY} icon="icon-external-link" onClick={openInNewTab} />;
  };

  return (
    <Lightbox isOpen={isOpen} onClose={onClose} isPartOfBackground={(target): boolean => isBesidePdfPage(target, mediaContainerRef.current)}>
      <div className={styles.lightboxHeader}>
        <div className={styles.left}>
          <Icon
            icon="icon-arrow-left"
            size={IconSize.SIZE_ICON_16}
            type={IconType.DARK_GHOST}
            shape={IconShape.SQUARE}
            ariaLabel={window.i18next.t('pmwjs_back')}
            onClick={onClose}
            className={styles.backButton}
          />
        </div>
        <div className={styles.headerActions}>
          {renderOpenInNewTabAction()}
          <Button
            text={window.i18next.t('pmwjs_download')}
            size={Size.XSMALL}
            type={Type.SECONDARY}
            icon="icon-download"
            isLoading={isDownloading}
            disabled={isDownloading}
            onClick={(): void => {
              void download();
            }}
          />
        </div>
      </div>
      <div ref={mediaContainerRef} className={styles.mediaContainer}>
        <PdfCanvas url={url} className={styles.pdfCanvas} />
      </div>
    </Lightbox>
  );
}
